import React, { useMemo, useEffect, useRef } from 'react';
import { Box, Stack, Typography, styled, keyframes } from '@mui/material';
import { SlateResponse } from '../common/store/types';
import SlateCard from './SlateCard';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Styled Components
const CalendarContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(0, 10, 22, 0.6)',
  borderRadius: 12,
  border: '1px solid rgba(1, 90, 206, 0.2)',
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  [theme.breakpoints.down('sm')]: {
    borderRadius: 0,
    border: 'none',
    borderTop: '1px solid rgba(1, 90, 206, 0.2)',
    boxShadow: 'none',
  },
}));

const TimeColumn = styled(Box)(({ theme }) => ({
  width: 70,
  flexShrink: 0,
  borderRight: '1px solid rgba(1, 90, 206, 0.15)',
  backgroundColor: 'rgba(0, 10, 22, 0.4)',
  [theme.breakpoints.down('sm')]: {
    width: 50,
  },
}));

const TimeLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Work Sans', sans-serif",
  fontSize: 12,
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.5)',
  padding: '8px 12px',
  height: 60,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  textAlign: 'right',
  [theme.breakpoints.down('sm')]: {
    fontSize: 10,
    padding: '8px 4px',
  },
}));

const DayColumn = styled(Box)<{ isToday?: boolean }>(({ theme, isToday }) => ({
  flex: 1,
  minWidth: 140,
  borderRight: '1px solid rgba(1, 90, 206, 0.1)',
  position: 'relative',
  backgroundColor: isToday ? 'rgba(1, 90, 206, 0.08)' : 'transparent',
  '&:last-child': {
    borderRight: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 'auto',
  },
}));

const DayHeader = styled(Box)<{ isToday?: boolean }>(({ theme, isToday }) => ({
  padding: '16px 12px',
  textAlign: 'center',
  borderBottom: '1px solid rgba(1, 90, 206, 0.2)',
  backgroundColor: isToday ? 'rgba(1, 90, 206, 0.15)' : 'rgba(0, 10, 22, 0.4)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: '12px 4px',
  },
}));

const DayName = styled(Typography)<{ isToday?: boolean }>(({ isToday }) => ({
  fontFamily: "'Work Sans', sans-serif",
  fontSize: 11,
  fontWeight: 600,
  color: isToday ? '#4DA3FF' : 'rgba(255, 255, 255, 0.5)',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginBottom: 4,
}));

const DayNumber = styled(Typography)<{ isToday?: boolean }>(({ isToday }) => ({
  fontFamily: "'Oswald', sans-serif",
  fontSize: 28,
  fontWeight: 600,
  color: isToday ? '#FFFFFF' : 'rgba(255, 255, 255, 0.8)',
  lineHeight: 1,
}));

const TodayIndicator = styled(Box)({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#FF6B35',
  margin: '8px auto 0',
  boxShadow: '0 0 8px rgba(255, 107, 53, 0.6)',
});

const TimeSlotRow = styled(Box)({
  height: 60,
  borderBottom: '1px solid rgba(1, 90, 206, 0.08)',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'rgba(1, 90, 206, 0.03)',
  },
});

const SlateEventsContainer = styled(Box)({
  position: 'absolute',
  top: 2,
  left: 4,
  right: 4,
  bottom: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  overflow: 'hidden',
});

const ScrollContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'auto',
  minHeight: 0,
  [theme.breakpoints.up('sm')]: {
    overflowY: 'scroll', // Force scrollbar on desktop for consistent width
  },
  '&::-webkit-scrollbar': {
    width: 8,
    height: 8,
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 10, 22, 0.4)',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(1, 90, 206, 0.4)',
    borderRadius: 4,
    '&:hover': {
      background: 'rgba(1, 90, 206, 0.6)',
    },
  },
}));

const NoEventsMessage = styled(Box)({
  padding: '60px 20px',
  textAlign: 'center',
  animation: `${fadeInUp} 0.5s ease-out`,
});

interface CalendarViewProps {
  viewMode: 'day' | 'week';
  currentDate: Date;
  slates: SlateResponse[];
  onSlateClick: (slate: SlateResponse) => void;
  onMultipleSlatesClick: (slates: SlateResponse[], hour: number) => void;
}

interface TimeSlot {
  hour: number;
  label: string;
}

interface DayData {
  date: Date;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
  slatesByHour: Map<number, SlateResponse[]>;
}

// Multiple Slates Card styled component
const MultipleSlatesCard = styled(Box)({
  backgroundColor: 'rgba(1, 90, 206, 0.2)',
  borderLeft: '3px solid #015ACE',
  borderRadius: 4,
  padding: '8px 12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(1, 90, 206, 0.3)',
    transform: 'translateX(2px)',
  },
});

const MultipleSlatesText = styled(Typography)({
  fontFamily: "'Work Sans', sans-serif",
  fontSize: 13,
  fontWeight: 600,
  color: '#FFFFFF',
  lineHeight: 1.2,
});

const MultipleSlatesCount = styled(Typography)({
  fontFamily: "'Work Sans', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
});

const CalendarView: React.FC<CalendarViewProps> = ({
  viewMode,
  currentDate,
  slates,
  onSlateClick,
  onMultipleSlatesClick,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Generate time slots (6 AM to 11 PM)
  const timeSlots: TimeSlot[] = useMemo(() => {
    const slots: TimeSlot[] = [];
    for (let hour = 6; hour <= 23; hour++) {
      const displayHour = hour > 12 ? hour - 12 : hour;
      const amPm = hour >= 12 ? 'PM' : 'AM';
      slots.push({
        hour,
        label: `${displayHour} ${amPm}`,
      });
    }
    return slots;
  }, []);

  // Generate days for the view
  const days: DayData[] = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const result: DayData[] = [];

    if (viewMode === 'day') {
      const dayDate = new Date(currentDate);
      dayDate.setHours(0, 0, 0, 0);

      const slatesByHour = new Map<number, SlateResponse[]>();
      slates.forEach((slate) => {
        const slateDate = new Date(slate.startTime);
        if (
          slateDate.getFullYear() === dayDate.getFullYear() &&
          slateDate.getMonth() === dayDate.getMonth() &&
          slateDate.getDate() === dayDate.getDate()
        ) {
          const hour = slateDate.getHours();
          if (!slatesByHour.has(hour)) {
            slatesByHour.set(hour, []);
          }
          slatesByHour.get(hour)!.push(slate);
        }
      });

      result.push({
        date: dayDate,
        dayName: dayDate.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNumber: dayDate.getDate(),
        isToday: dayDate.getTime() === today.getTime(),
        slatesByHour,
      });
    } else {
      // Week view
      const startOfWeek = new Date(currentDate);
      const dayOfWeek = startOfWeek.getDay();
      startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
      startOfWeek.setHours(0, 0, 0, 0);

      for (let i = 0; i < 7; i++) {
        const dayDate = new Date(startOfWeek);
        dayDate.setDate(startOfWeek.getDate() + i);

        const slatesByHour = new Map<number, SlateResponse[]>();
        slates.forEach((slate) => {
          const slateDate = new Date(slate.startTime);
          if (
            slateDate.getFullYear() === dayDate.getFullYear() &&
            slateDate.getMonth() === dayDate.getMonth() &&
            slateDate.getDate() === dayDate.getDate()
          ) {
            const hour = slateDate.getHours();
            if (!slatesByHour.has(hour)) {
              slatesByHour.set(hour, []);
            }
            slatesByHour.get(hour)!.push(slate);
          }
        });

        result.push({
          date: dayDate,
          dayName: dayDate.toLocaleDateString('en-US', { weekday: 'short' }),
          dayNumber: dayDate.getDate(),
          isToday: dayDate.getTime() === today.getTime(),
          slatesByHour,
        });
      }
    }

    return result;
  }, [viewMode, currentDate, slates]);

  // Check if any slates exist for the current view
  const hasSlates = days.some((day) => day.slatesByHour.size > 0);

  // Calculate earliest hour with slates in current view
  const earliestVisibleHour = useMemo(() => {
    let earliest = 23;
    days.forEach((day) => {
      day.slatesByHour.forEach((_, hour) => {
        if (hour < earliest) {
          earliest = hour;
        }
      });
    });
    return earliest >= 6 ? earliest : 6;
  }, [days]);

  // Scroll to earliest slate when view changes or data loads
  useEffect(() => {
    if (scrollContainerRef.current && hasSlates) {
      const timeSlotHeight = 60;
      const scrollOffset = (earliestVisibleHour - 6) * timeSlotHeight;
      // Use smooth scroll for better UX
      scrollContainerRef.current.scrollTo({
        top: Math.max(0, scrollOffset),
        behavior: 'smooth',
      });
    }
  }, [earliestVisibleHour, hasSlates, viewMode, currentDate]);

  return (
    <CalendarContainer>
      <Stack direction="row" sx={{ flexShrink: 0, paddingRight: { xs: 0, sm: '8px' }, alignItems: 'stretch' }}>
        {/* Time column header spacer */}
        <TimeColumn sx={{ display: 'flex' }}>
          <DayHeader sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: "'Work Sans', sans-serif",
                fontSize: 11,
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.4)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              Time
            </Typography>
          </DayHeader>
        </TimeColumn>

        {/* Day columns headers */}
        {days.map((day, index) => (
          <DayColumn key={day.date.toISOString()} isToday={day.isToday} sx={{ display: 'flex' }}>
            <DayHeader
              isToday={day.isToday}
              sx={{
                flex: 1,
                animation: `${slideIn} 0.3s ease-out`,
                animationDelay: `${index * 0.05}s`,
                animationFillMode: 'both',
              }}
            >
              <DayName isToday={day.isToday}>{day.dayName}</DayName>
              <DayNumber isToday={day.isToday}>{day.dayNumber}</DayNumber>
              {day.isToday && <TodayIndicator />}
            </DayHeader>
          </DayColumn>
        ))}
      </Stack>

      <ScrollContainer ref={scrollContainerRef}>
        {!hasSlates ? (
          <NoEventsMessage>
            <Typography
              sx={{
                fontFamily: "'Oswald', sans-serif",
                fontSize: 24,
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.4)',
                marginBottom: 1,
              }}
            >
              No Slates Scheduled
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Work Sans', sans-serif",
                fontSize: 14,
                color: 'rgba(255, 255, 255, 0.3)',
              }}
            >
              There are no slates available for this time period
            </Typography>
          </NoEventsMessage>
        ) : (
          timeSlots.map((slot) => (
            <Stack direction="row" key={slot.hour}>
              <TimeColumn>
                <TimeLabel>{slot.label}</TimeLabel>
              </TimeColumn>

              {days.map((day) => {
                const slatesToShow = day.slatesByHour.get(slot.hour) || [];

                return (
                  <DayColumn key={day.date.toISOString()} isToday={day.isToday}>
                    <TimeSlotRow>
                      {slatesToShow.length === 1 && (
                        <SlateEventsContainer>
                          <SlateCard
                            slate={slatesToShow[0]}
                            onClick={() => onSlateClick(slatesToShow[0])}
                            compact={false}
                            animationDelay={0}
                          />
                        </SlateEventsContainer>
                      )}
                      {slatesToShow.length > 1 && (
                        <SlateEventsContainer>
                          <MultipleSlatesCard
                            onClick={() => onMultipleSlatesClick(slatesToShow, slot.hour)}
                          >
                            <MultipleSlatesText>
                              Multiple Slates
                            </MultipleSlatesText>
                            <MultipleSlatesCount>
                              {slatesToShow.length} slates starting
                            </MultipleSlatesCount>
                          </MultipleSlatesCard>
                        </SlateEventsContainer>
                      )}
                    </TimeSlotRow>
                  </DayColumn>
                );
              })}
            </Stack>
          ))
        )}
      </ScrollContainer>
    </CalendarContainer>
  );
};

export default CalendarView;
